/* eslint-disable multiline-ternary */
// ** React Imports
import { Fragment, lazy } from 'react'
import { Navigate } from 'react-router-dom'
// ** Layouts
import BlankLayout from '@layouts/BlankLayout'
import VerticalLayout from '@src/layouts/VerticalLayout'
import HorizontalLayout from '@src/layouts/HorizontalLayout'
import LayoutWrapper from '@src/@core/layouts/components/layout-wrapper'

// ** Route Components
import PublicRoute from '@components/routes/PublicRoute'
import PrivateRoute from '@components/routes/PrivateRoute'

// ** Utils
import { isObjEmpty } from '@utils'
import DocumentsList from '../../views/pages/documents/documents-list'
import SignDocument from '../../views/pages/sign-document/sign-document'
import TemplateList from '../../views/pages/templates/template-list'
import EditTemplate from '../../views/pages/templates/components/edit-template/template-edit'
import SignersList from '../../views/pages/signers/signers-list'
import CompaniesList from '../../views/pages/companies/companies-list'
import SocietiesList from '../../views/pages/societies'
import SignatoriesList from '../../views/pages/signatories-societies'
import UserList from '../../views/pages/users/user-list'
import AreaList from '../../views/pages/area/area-list'
import ChangePassword from '../../views/pages/authentication/change-password'
import InvoicesViewer from '../../views/pages/invoices-viewer/invoices-viewer'
import Invoices from '../../views/pages/invoices/Invoices'
import DocsError from '../../views/pages/invoices-error/Invoices'
import InvoiceIssuance from '../../views/pages/invoice-issuance/invoiceIssuance'
import CustomerMasterLatitude from '../../views/pages/customer-master-latitude'
import CustomerMaster from '../../views/pages/customer-master'
import ProductServiceMaster from '../../views/pages/product-services-master'
import BusinessManager from '../../views/pages/business-manager'
import DownloadInvoices from '../../views/pages/download-invoices/download-invoices'
import SalesBook from '../../views/pages/sales-book'
import EndCustomer from '../../views/pages/end-customer'
import CompanyPositionsList from '../../views/pages/company-positions'
import ReportsManager from '../../views/pages/reports'
import ReceptionDocuments from '../../views/pages/reception-documents'

const getLayout = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
  horizontal: <HorizontalLayout />,
}

// ** Document title
const TemplateTitle = '%s - Vuexy React Admin Template'

// ** Default Route
const DefaultRoute = '/home'
const ChangePasswordRoute = '/changepassword'

const Home = lazy(() => import('../../views/home'))
const HomeCameraOne = lazy(() => import('../../views/home.camera'))
const SignatureDiagram = lazy(() =>
  import('../../views/pages/signature/signature-diagram')
)
const Login = lazy(() => import('../../views/pages/authentication/login'))
const Error = lazy(() => import('../../views/error'))
const NotAuthorized = lazy(() =>
  import('../../views/pages/misc/not-authorized')
)

// ** Merge Routes
const Routes = [
  {
    path: '/',
    index: true,
    element: (
      <Navigate
        replace
        to={DefaultRoute}
      />
    ),
  },
  {
    path: '/home',
    element: <Home />,
  },
  {
    path: '/home-camera',
    element: <HomeCameraOne />,
  },
  {
    path: '/documents/:statusDocument',
    element: <DocumentsList />,
  },
  {
    path: '/templates',
    element: <TemplateList />,
  },
  {
    path: '/users',
    element: <UserList />,
  },
  {
    path: '/signers',
    element: <SignersList />,
  },
  {
    path: '/area',
    element: <AreaList />,
  },
  {
    path: '/societies',
    element: <SocietiesList />,
  },
  {
    path: '/signatories-societies',
    element: <SignatoriesList />,
  },
  {
    path: '/company-positions',
    element: <CompanyPositionsList />,
  },
  {
    path: '/companies',
    element: <CompaniesList />,
  },
  {
    path: '/signature',
    element: <SignatureDiagram />,
  },
  {
    path: '/login',
    element: <Login />,
    meta: {
      layout: 'blank',
      publicRoute: true,
    },
  },
  {
    path: '/sign-document/:documentNames/:clientName/:documentAwsId/:token',
    element: <SignDocument />,
    meta: {
      layout: 'blank',
      publicRoute: true,
    },
  },
  {
    path: '/invoices',
    element: <Invoices />,
  },
  {
    path: '/customer-master-latitude',
    element: <CustomerMasterLatitude />,
  },
  {
    path: '/docs-error',
    element: <DocsError />,
  },
  {
    path: '/invoices-viewer',
    element: <InvoicesViewer />,
    meta: {
      layout: 'blank',
      publicRoute: true,
    },
  },
  {
    path: '/error',
    element: <Error />,
    meta: {
      layout: 'blank',
    },
  },
  {
    path: '/not-authorized',
    element: <NotAuthorized />,
    meta: {
      publicRoute: true,
      layout: 'blank',
    },
  },

  {
    path: '/changepassword',
    element: <ChangePassword />,
    meta: {
      publicRoute: true,
      layout: 'blank',
    },
  },
  // {
  //   path: '/test',
  //   element: <TestComponentDate />,
  // },
  {
    path: '/edit-template/:templateId',
    element: <EditTemplate />,
  },

  /* Billing */
  {
    path: '/download-invoices/:documentId/:token',
    element: <DownloadInvoices />,
    meta: {
      layout: 'blank',
      publicRoute: true,
    },
  },
  {
    path: '/sales-book',
    element: <SalesBook />,
  },
  {
    path: '/end-customer',
    element: <EndCustomer />,
  },
  {
    path: '/invoice-issuance',
    element: <InvoiceIssuance />,
  },
  {
    path: '/debit-credit-note/:invoiceId',
    element: <InvoiceIssuance />,
  },
  {
    path: '/debit-credit-note/:invoiceId',
    element: <InvoiceIssuance />,
  },
  {
    path: '/customer-master',
    element: <CustomerMaster />,
  },
  {
    path: '/product-service-master',
    element: <ProductServiceMaster />,
  },
  {
    path: '/business-manager',
    element: <BusinessManager />,
  },
  {
    path: '/reports',
    element: <ReportsManager />,
  },
  {
    path: '/reception-documents',
    element: <ReceptionDocuments />,
  },
]

const getRouteMeta = (route) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta }
    } else {
      return {}
    }
  }
}

// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout, defaultLayout) => {
  const LayoutRoutes = []
  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false
      // ** Checks if Route layout or Default layout matches current layout
      if (
        (route.meta &&
          route.meta.layout &&
          route.meta.layout === layout) ||
        ((route.meta === undefined || route.meta.layout === undefined) &&
          defaultLayout === layout)
      ) {
        let RouteTag = PrivateRoute

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === 'blank'
            ? (isBlank = true)
            : (isBlank = false)
          RouteTag = route.meta.publicRoute ? PublicRoute : PrivateRoute
        }

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === 'blank'
            ? (isBlank = true)
            : (isBlank = false)
        }
        if (route.element) {
          const Wrapper =
            // eslint-disable-next-line multiline-ternary
            isObjEmpty(route.element.props) && isBlank === false
              ? // eslint-disable-next-line multiline-ternary
                LayoutWrapper
              : Fragment

          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          )
        }

        // Push route to LayoutRoutes
        LayoutRoutes.push(route)
      }
      return LayoutRoutes
    })
  }
  return LayoutRoutes
}

const getRoutes = (layout) => {
  const defaultLayout = layout || 'vertical'
  const layouts = ['vertical', 'horizontal', 'blank']

  const AllRoutes = []

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout)

    AllRoutes.push({
      path: '/',
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes,
    })
  })
  return AllRoutes
}

export {
  DefaultRoute,
  TemplateTitle,
  Routes,
  getRoutes,
  ChangePasswordRoute,
}
